import { Button } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ArrowLeft from 'components/Icons/ArrowLeft'
import { TooltipWhenDisabled } from 'components/TooltipWhenDisabled'
import React from 'react'
import { Translate } from 'react-redux-i18n'
import palette from '../constants/palette'
import Spinner from './Spinner'

type Props = {
  canProceed?: boolean
  className?: string
  hideCancel?: boolean
  title?: string
  subtitle?: string
  children?: React.ReactNode
  buttonGroupChildren?: React.ReactNode
  onProceed?: () => void
  onClose?: () => void
  proceedText?: string
  // Hover/focus text naming why Confirm is disabled. Wraps both proceed
  // buttons in TooltipWhenDisabled so it's reachable by keyboard, not just
  // mouse hover (mirrors WizardNavButtons' continueTooltip). Omit for the
  // (more common) disabled reasons that don't have dedicated copy — the
  // button then renders unwrapped, unchanged.
  proceedTooltip?: string
  isOpen: boolean
  isLoading?: boolean
  width?: string
  onBack?: VoidFunction
  showTopProceed?: boolean
  showBottomProceed?: boolean
  subtitleClassName?: string
  dialogStyle?: object
  position?: 'top' | 'center' | 'bottom'
  zIndex?: number
  zeroSpacing?: boolean
  /** Accessible name for the dialog. Base Web defaults to the generic
   *  literal "dialog"; supply this when the modal has no visible heading
   *  the dialog node can reference via aria-labelledby. */
  ariaLabel?: string
  /** Base Web focuses the dialog's first focusable descendant on open.
   *  Set false when that descendant is a hover/focus-triggered control
   *  (e.g. an InfoTooltip icon) whose own overlay would then pop open
   *  unprompted — the focus trap (focusLock) still applies either way. */
  autoFocus?: boolean
}

const ModalContainer: React.FC<Props> = ({
  canProceed = true,
  children = null,
  buttonGroupChildren = null,
  className = '',
  hideCancel = false,
  onClose,
  onProceed,
  title,
  subtitle,
  proceedText,
  proceedTooltip,
  isOpen,
  isLoading = false,
  width = '600px',
  onBack,
  showTopProceed = false,
  showBottomProceed = true,
  subtitleClassName = '',
  dialogStyle = {},
  position,
  zIndex = 3,
  zeroSpacing = false,
  ariaLabel,
  autoFocus,
}) => {
  const [css] = useStyletron()
  // Resolve the accessible name in this order: explicit `ariaLabel`
  // (call sites with context-rich copy like "Location details for X")
  // → `title` (most modals render a heading the operator can read on
  // screen, mirror it to AT) → undefined (Base Web falls back to the
  // generic literal "dialog", which is the case we'd rather never
  // hit). Pushing the fallback into the component means new call
  // sites can't ship a "dialog"-labelled modal by accident.
  const resolvedAriaLabel = ariaLabel || title
  return (
    <Modal
      overrides={{
        Root: {
          style: {
            zIndex,
          },
        },
        Dialog: {
          props: resolvedAriaLabel
            ? { 'aria-label': resolvedAriaLabel }
            : undefined,
          style: () => ({
            borderRadius: '4px',
            border: 'none',
            boxShadow: '0px 4px 8px rgba(54, 66, 78, 0.3)',
            width,
            ...dialogStyle,
          }),
        },
      }}
      closeType="small"
      position={position}
      size="auto"
      isOpen={isOpen}
      onClose={onClose}
      autoFocus={autoFocus}
    >
      <div className={`${!zeroSpacing ? 'p-xl' : ''} ${className}`}>
        <Switch condition={!!onBack}>
          <Button
            kind="minimal"
            className={`pl-0 mb-s ${css({ color: palette.grey90 })}`}
            onClick={onBack}
          >
            <span className="mr-s">
              <ArrowLeft />
            </span>
            <Translate value="general.buttons.back" />
          </Button>
        </Switch>
        <Switch condition={!!title}>
          <Text.Header3 tag="div" color="grey90" className="mb-xs">
            {title}
          </Text.Header3>
        </Switch>
        <Switch condition={!!subtitle}>
          <Text.BodyText4
            tag="div"
            color="grey70"
            className={subtitleClassName}
          >
            {subtitle}
          </Text.BodyText4>
        </Switch>
        <Switch condition={showTopProceed}>
          <TooltipWhenDisabled
            disabled={Boolean(proceedTooltip)}
            tooltip={proceedTooltip ?? ''}
          >
            <Button
              disabled={!canProceed}
              className="mt-s"
              type="button"
              kind="primary"
              onClick={onProceed}
            >
              <Spinner isLoading={isLoading}>{proceedText}</Spinner>
            </Button>
          </TooltipWhenDisabled>
        </Switch>
        {children}
        <Switch
          condition={
            !!buttonGroupChildren || !!showBottomProceed || !hideCancel
          }
        >
          <div className="mt-xl d-flex justify-content-end flex-align-center">
            {buttonGroupChildren}
            <Switch condition={!hideCancel}>
              <Button type="button" kind="secondary" onClick={onClose}>
                <Translate value="thanx_campaigns.builder.variants.cancel" />
              </Button>
            </Switch>
            {showBottomProceed && (
              <TooltipWhenDisabled
                disabled={Boolean(proceedTooltip)}
                tooltip={proceedTooltip ?? ''}
              >
                <Button
                  disabled={!canProceed}
                  className="ml-s"
                  type="button"
                  kind="primary"
                  onClick={onProceed}
                  isLoading={isLoading}
                >
                  {proceedText}
                </Button>
              </TooltipWhenDisabled>
            )}
          </div>
        </Switch>
      </div>
    </Modal>
  )
}

export default ModalContainer
